import { useCallback, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import type { RemoveUploadedHandler, UploadedItem, UploadFileHandler, UploadItem } from './types';
import { UPLOAD_ITEM_STATUS } from './types';

export const useUploadQueue = (
    uploadFile: UploadFileHandler,
    removeUploaded: RemoveUploadedHandler,
    onError: (message: string, fileName: string) => void,
) => {
    const [items, setItems] = useState<UploadItem[]>([]);
    const controllers = useRef<Map<string, AbortController>>(new Map());
    // Fully sent uploads can't be aborted (the server stores them anyway); they are deleted once they finish
    const discardOnFinish = useRef<Set<string>>(new Set());
    const inFlight = useRef<Map<string, Promise<{ file: File } | null>>>(new Map());

    const add = useCallback(
        (files: File[]) => {
            files.forEach((file) => {
                const id = crypto.randomUUID();
                const controller = new AbortController();
                controllers.current.set(id, controller);
                setItems((prev) => [...prev, { file, id, progress: 0, status: UPLOAD_ITEM_STATUS.UPLOADING }]);

                const task = uploadFile(file, {
                    onProgress: (percent) =>
                        setItems((prev) =>
                            prev.map((item) =>
                                item.id === id && item.status === UPLOAD_ITEM_STATUS.UPLOADING
                                    ? { ...item, progress: percent }
                                    : item,
                            ),
                        ),
                    signal: controller.signal,
                })
                    .then(async (uploaded): Promise<{ file: File } | null> => {
                        controllers.current.delete(id);
                        if (discardOnFinish.current.delete(id)) {
                            setItems((prev) => prev.filter((item) => item.id !== id));
                            try {
                                await removeUploaded(uploaded);
                                return null;
                            } catch (error: unknown) {
                                Sentry.captureException(error, { extra: { fileName: file.name } });
                                return { file };
                            }
                        }
                        // Cancelled after the server stored the file; roll it back
                        if (controller.signal.aborted) {
                            removeUploaded(uploaded).catch((error: unknown) => {
                                Sentry.captureException(error, { extra: { fileName: file.name } });
                                onError(error instanceof Error ? error.message : String(error), file.name);
                            });
                            return null;
                        }
                        setItems((prev) =>
                            prev.map(
                                (item): UploadItem =>
                                    item.id === id
                                        ? {
                                              file: item.file,
                                              id: item.id,
                                              removing: false,
                                              status: UPLOAD_ITEM_STATUS.SUCCESS,
                                              uploaded,
                                          }
                                        : item,
                            ),
                        );
                        return null;
                    })
                    .catch((error: unknown) => {
                        controllers.current.delete(id);
                        discardOnFinish.current.delete(id);
                        // A cancelled upload already had its row removed — don't report it as a failure.
                        if (controller.signal.aborted) return null;
                        setItems((prev) => prev.filter((item) => item.id !== id));
                        Sentry.captureException(error, { extra: { fileName: file.name } });
                        onError(error instanceof Error ? error.message : String(error), file.name);
                        return null;
                    })
                    .finally(() => inFlight.current.delete(id));
                inFlight.current.set(id, task);
            });
        },
        [uploadFile, removeUploaded, onError],
    );

    const cancel = useCallback((id: string) => {
        controllers.current.get(id)?.abort();
        controllers.current.delete(id);
        setItems((prev) => prev.filter((item) => item.id !== id));
    }, []);

    const remove = useCallback(
        async (item: UploadedItem) => {
            setItems((prev) =>
                prev.map((entry) =>
                    entry.id === item.id && entry.status === UPLOAD_ITEM_STATUS.SUCCESS
                        ? { ...entry, removing: true }
                        : entry,
                ),
            );
            try {
                await removeUploaded(item.uploaded);
                setItems((prev) => prev.filter((entry) => entry.id !== item.id));
            } catch (error: unknown) {
                setItems((prev) =>
                    prev.map((entry) =>
                        entry.id === item.id && entry.status === UPLOAD_ITEM_STATUS.SUCCESS
                            ? { ...entry, removing: false }
                            : entry,
                    ),
                );
                Sentry.captureException(error, { extra: { fileName: item.file.name } });
                onError(error instanceof Error ? error.message : String(error), item.file.name);
            }
        },
        [removeUploaded, onError],
    );

    const reset = useCallback(() => {
        controllers.current.forEach((controller) => controller.abort());
        controllers.current.clear();
        setItems([]);
    }, []);

    const rollback = useCallback(async (): Promise<{ file: File }[]> => {
        // Rows still uploading below 100 % are aborted; the rest are awaited and deleted once stored
        const processing = items.filter((item) => item.status === UPLOAD_ITEM_STATUS.UPLOADING && item.progress >= 100);
        processing.forEach((item) => discardOnFinish.current.add(item.id));
        const processingResults = Promise.all(processing.map((item) => inFlight.current.get(item.id) ?? null));

        const uploadedItems = items.filter(
            (item): item is UploadedItem => item.status === UPLOAD_ITEM_STATUS.SUCCESS && !item.removing,
        );
        const rollingBackIds = new Set(uploadedItems.map((item) => item.id));
        setItems((prev) => prev.map((item) => (rollingBackIds.has(item.id) ? { ...item, removing: true } : item)));

        const results = await Promise.allSettled(uploadedItems.map((item) => removeUploaded(item.uploaded)));

        const failed = uploadedItems.filter((item, index) => {
            const result = results[index];
            if (result.status === 'fulfilled') return false;
            Sentry.captureException(result.reason, { extra: { fileName: item.file.name } });
            return true;
        });
        const failedIds = new Set(failed.map((item) => item.id));
        setItems((prev) =>
            prev
                .filter((item) => !rollingBackIds.has(item.id) || failedIds.has(item.id))
                .map((item) => (failedIds.has(item.id) ? { ...item, removing: false } : item)),
        );

        const discardFailures = (await processingResults).filter((result) => result !== null);
        return [...failed, ...discardFailures];
    }, [items, removeUploaded]);

    const isUploading = items.some((item) => item.status === UPLOAD_ITEM_STATUS.UPLOADING);

    return { add, cancel, isUploading, items, remove, reset, rollback };
};
